import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Sparkles,
  Crown,
  ArrowRight,
  Loader2,
  Check,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { trackEvent } from "@/lib/posthog";

import { STRIPE_PLANS, PRO_PRODUCT_ID, STARTER_PRODUCT_ID } from "@/lib/stripe-constants";

/** Fonctionnalités Pro perdues lors d'un passage Pro → Starter. */
const PRO_FEATURES_LOST = [
  "Domaine personnalisé",
  "Suppression du branding Magnetiq",
  "Générateur UTM",
  "Identité de marque (Ma marque)",
  "Analytics détaillés",
  "Export CSV des leads",
  "Support prioritaire",
];

/** Hardcoded fallback — used only while pricing_config hasn't loaded yet */
const FALLBACK_PLANS = {
  starter: {
    priceId: STRIPE_PLANS.starter.priceId,
    productId: STRIPE_PLANS.starter.productId,
    name: STRIPE_PLANS.starter.name,
    price: STRIPE_PLANS.starter.price,
    badge: "Le plus populaire",
    features: [
      "Landing pages illimitées",
      "Sous-domaine personnalisé",
      "Intégration Brevo",
      "Champs personnalisables",
      "Templates avec duplication",
      "Preview mobile",
      "Page Link in Bio",
      "Dashboard analytics",
      "Hébergement sécurisé + SSL",
    ],
  },
  pro: {
    priceId: STRIPE_PLANS.pro.priceId,
    productId: STRIPE_PLANS.pro.productId,
    name: STRIPE_PLANS.pro.name,
    price: STRIPE_PLANS.pro.price,
    badge: "Croissance",
    features: [
      "Tout le plan Starter",
      "Domaine personnalisé",
      "Suppression du branding",
      "Générateur UTM",
      "Identité de marque personnalisée",
      "Analytics détaillés",
      "Export CSV",
      "Support prioritaire",
    ],
  },
} as const;

const ICON_MAP: Record<string, typeof Sparkles> = {
  starter: Sparkles,
  pro: Crown,
};

interface DynamicPlan {
  priceId: string;
  productId: string;
  name: string;
  price: number;
  badge: string;
  features: string[];
}

interface PlanSelectionModalProps {
  open: boolean;
  defaultPlan?: string;
  onClose: () => void;
  closable?: boolean;
  currentProductId?: string | null;
  onPlanChanged?: () => void;
  trialExpired?: boolean;
}

export default function PlanSelectionModal({
  open,
  defaultPlan,
  onClose,
  closable = true,
  currentProductId,
  onPlanChanged,
  trialExpired = false,
}: PlanSelectionModalProps) {
  const [plans, setPlans] = useState<Record<string, DynamicPlan>>(() => {
    // Build initial from fallback
    const init: Record<string, DynamicPlan> = {};
    for (const [key, p] of Object.entries(FALLBACK_PLANS)) {
      init[key] = { priceId: p.priceId, productId: p.productId, name: p.name, price: p.price, badge: p.badge, features: [...p.features] };
    }
    return init;
  });

  // Fetch dynamic pricing from pricing_config
  useEffect(() => {
    if (!open) return;
    supabase
      .from("pricing_config")
      .select("*")
      .order("price_amount", { ascending: true })
      .then(({ data }) => {
        if (!data || data.length === 0) return;
        const dynamic: Record<string, DynamicPlan> = {};
        for (const row of data) {
          const fallback = FALLBACK_PLANS[row.plan_key as keyof typeof FALLBACK_PLANS];
          dynamic[row.plan_key] = {
            priceId: row.price_id,
            productId: row.product_id,
            name: row.label,
            price: row.price_amount / 100,
            badge: row.badge || fallback?.badge || "",
            features: Array.isArray(row.features) && row.features.length > 0
              ? (row.features as string[])
              : fallback?.features ? [...fallback.features] : [],
          };
        }
        setPlans(dynamic);
      });
  }, [open]);

  const planKeys = Object.keys(plans);
  type PlanKey = string;

  const isChangeMode = !!currentProductId;
  const currentPlanKey = isChangeMode
    ? planKeys.find((k) => plans[k].productId === currentProductId)
    : undefined;

  const [selected, setSelected] = useState<PlanKey>(
    defaultPlan && plans[defaultPlan] ? defaultPlan : "starter"
  );
  const [loading, setLoading] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const selectedPlan = plans[selected] || plans.starter;

  // Downgrade Pro → Starter : on avertit explicitement des fonctions perdues.
  const isDowngrade =
    isChangeMode &&
    currentProductId === PRO_PRODUCT_ID &&
    selectedPlan.productId === STARTER_PRODUCT_ID;

  const handleConfirm = async () => {
    setLoading(true);
    trackEvent("checkout_initiated", { plan: selected, mode: isChangeMode ? "change" : "new" });
    try {
      if (isChangeMode) {
        const { data, error } = await supabase.functions.invoke("change-plan", {
          body: { priceId: selectedPlan.priceId },
        });
        if (error) throw error;
        if (data?.error) throw new Error(data.error);
        toast.success(`Plan changé pour ${selectedPlan.name} !`);
        onPlanChanged?.();
        onClose();
      } else {
        const { data, error } = await supabase.functions.invoke("create-checkout", {
          body: { priceId: selectedPlan.priceId },
        });
        if (error) throw error;
        if (data?.url) {
          window.open(data.url, "_blank");
          onClose();
        }
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Erreur";
      toast.error(isChangeMode ? `Erreur lors du changement de plan : ${msg}` : "Erreur lors de la création du paiement. Veuillez réessayer.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
    <Dialog open={open} onOpenChange={(v) => { if (!v && closable) onClose(); }}>
      <DialogContent
        className={cn("sm:max-w-lg", !closable && "[&>button.absolute]:hidden")}
        onInteractOutside={(e) => { if (!closable) e.preventDefault(); }}
        onEscapeKeyDown={(e) => { if (!closable) e.preventDefault(); }}
      >
        <DialogHeader>
          <DialogTitle className="text-xl">
            {isChangeMode ? "Changer de plan" : trialExpired ? "Votre essai Pro est terminé" : "Choisissez votre plan"}
          </DialogTitle>
          <DialogDescription>
            {isChangeMode
              ? "Sélectionnez votre nouveau plan. Le prorata sera calculé automatiquement."
              : trialExpired
                ? "Votre essai Pro gratuit de 7 jours est terminé. Choisissez un plan pour continuer à utiliser Magnetiq."
                : "Sélectionnez le plan qui vous convient, puis confirmez pour accéder au paiement sécurisé."}
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
          {planKeys.map((key) => {
            const plan = plans[key];
            const isCurrent = key === currentPlanKey;
            const isSelected = selected === key;
            const Icon = ICON_MAP[key] || Sparkles;
            return (
              <button
                key={key}
                onClick={() => { if (!isCurrent) { setSelected(key); trackEvent("plan_selected", { plan: key }); } }}
                disabled={isCurrent}
                className={cn(
                  "relative rounded-xl border-2 p-4 text-left transition-all",
                  isCurrent
                    ? "border-muted bg-muted/30 opacity-60 cursor-not-allowed"
                    : isSelected
                      ? "border-primary bg-primary/5"
                      : "border-border hover:border-primary/40"
                )}
              >
                {isCurrent && (
                  <div className="absolute top-2 right-2">
                    <Badge variant="outline" className="text-[9px]">Plan actuel</Badge>
                  </div>
                )}
                {!isCurrent && isSelected && (
                  <div className="absolute top-2 right-2">
                    <Check className="h-4 w-4 text-primary" />
                  </div>
                )}
                <div className="flex items-center gap-2 mb-2">
                  <Icon className="h-4 w-4 text-primary" />
                  <span className="font-['Bricolage_Grotesque'] font-extrabold">{plan.name}</span>
                  {!isCurrent && (
                    <Badge
                      variant={key === "starter" ? "default" : "secondary"}
                      className="text-[9px] ml-auto"
                    >
                      {plan.badge}
                    </Badge>
                  )}
                </div>
                <div className="flex items-baseline gap-1 mb-3">
                  <span className="font-['Bricolage_Grotesque'] text-2xl font-extrabold">{plan.price}€</span>
                  <span className="text-xs text-muted-foreground">/mois</span>
                </div>
                <ul className="space-y-1.5">
                  {plan.features.map((f) => (
                    <li
                      key={f}
                      className="flex items-start gap-2 text-xs text-muted-foreground"
                    >
                      <Check className="h-3 w-3 text-primary mt-0.5 shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>
              </button>
            );
          })}
        </div>

        <Button
          className="w-full mt-4 bg-highlight text-highlight-foreground hover:bg-highlight/90"
          size="lg"
          onClick={isChangeMode ? () => setShowConfirm(true) : handleConfirm}
          disabled={loading || selected === currentPlanKey}
        >
          {loading ? (
            <Loader2 className="h-4 w-4 animate-spin mr-2" />
          ) : null}
          {isChangeMode
            ? `Passer au plan ${selectedPlan.name}`
            : `Continuer avec ${selectedPlan.name}`}
          <ArrowRight className="ml-2 h-4 w-4" />
        </Button>

        {!isChangeMode && (
          <p className="text-[11px] text-muted-foreground text-center">
            Annulable à tout moment
          </p>
        )}
        {isChangeMode && (
          <p className="text-[11px] text-muted-foreground text-center">
            Le changement prend effet immédiatement · Prorata automatique
          </p>
        )}
      </DialogContent>
    </Dialog>

    <AlertDialog open={showConfirm} onOpenChange={setShowConfirm}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {isDowngrade ? "Confirmer le passage à Starter" : "Confirmer le changement de plan"}
          </AlertDialogTitle>
          <AlertDialogDescription>
            Vous allez passer au plan <strong>{selectedPlan.name}</strong> à {selectedPlan.price}€/mois.
            Le prorata sera calculé automatiquement. Cette action prend effet immédiatement.
          </AlertDialogDescription>
          {isDowngrade && (
            <div className="mt-3 rounded-lg border border-amber-500/30 bg-amber-500/5 p-3">
              <p className="text-sm font-medium text-amber-600 mb-2">
                En repassant à Starter, vous perdez l'accès à :
              </p>
              <ul className="space-y-1">
                {PRO_FEATURES_LOST.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-xs text-muted-foreground">
                    <span className="text-amber-500 mt-0.5">✕</span>
                    {f}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={loading}>Annuler</AlertDialogCancel>
          <AlertDialogAction onClick={handleConfirm} disabled={loading}>
            {loading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            Confirmer
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </>
  );
}
